/**
 * useSidebarToggleShortcut
 *
 * Binds the global ⌘B / Ctrl+B chord to the sidebar collapse/expand
 * action. Mirrors the convention used by VS Code, Linear, Notion,
 * Slack — primary-mod + B toggles the side panel.
 *
 * Wires through the central `keyboardShortcuts` registry (the matcher
 * lives there) so the chord shows up in the keyboard shortcuts dialog
 * with the right platform-formatted label.
 *
 * Mounted once at the root layout. Ignores keystrokes coming from
 * editors and rich-text areas so it doesn't hijack user typing (where
 * Ctrl+B is the native bold-text chord).
 */

import { useEffect } from 'react';
import { shouldSkipShortcut, matchesShortcut } from '@/utils/input';
import { useUIPreferencesStore } from '@/stores/uiPreferencesStore';
import { APP_SHELL_MOBILE_QUERY } from '@/theme/appShell';

export function useSidebarToggleShortcut(): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (shouldSkipShortcut(event)) return;
      if (!matchesShortcut(event, 'toggleSidebar')) return;
      if (typeof window.matchMedia === 'function' && window.matchMedia(APP_SHELL_MOBILE_QUERY).matches) return;
      // We own this chord — suppress browser default (Safari maps ⌘B
      // to "show bookmarks bar"; some sites also use it for bold) and
      // only toggle once per press.
      event.preventDefault();
      if (event.repeat) return;
      useUIPreferencesStore.getState().toggleSidebar();
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}
